/**
 * ValidationRulesEditor Component
 * 
 * Comprehensive editor for all validation rules organized by category.
 */

import React, { useState } from 'react';
import { Collapse, Button, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ValidationRuleEditor } from './ValidationRuleEditor';
import { ValidationRules, ValidationRule } from '../../types/transitionParameters';
import './ValidationRulesEditor.css';

const { Panel } = Collapse;

interface PropertyDefinition {
  property_key?: string;
  name?: string;
  property_type?: string;
  type?: string;
  description?: string;
}

interface TransitionOption {
  name: string;
  id?: string;
  description?: string;
}

interface ValidationRulesEditorProps {
  validationRules: ValidationRules;
  onChange: (rules: ValidationRules) => void;
  disabled?: boolean;
  artifactProperties?: PropertyDefinition[]; // Properties available for property-based rules
  transitions?: TransitionOption[]; // Available transitions for selection (e.g., for improvement artifact check)
}

export const ValidationRulesEditor: React.FC<ValidationRulesEditorProps> = ({
  validationRules,
  onChange,
  disabled = false,
  artifactProperties = [],
  transitions = [],
}) => {
  const [activeKey, setActiveKey] = useState<string[]>(
    artifactProperties.length > 0 ? ['property_validation'] : ['conversion']
  );

  const categories = [
    ...(artifactProperties.length > 0 ? [{
      key: 'property_validation',
      title: 'Transition Conditions',
      description: 'Property-based conditions that must be met before transition can proceed (e.g., improvement_is_validated = true AND today is between effective dates)',
      rules: {} as Record<string, string>, // All rules are custom
      priority: true, // Show this first
    }] : []),
    {
      key: 'demand_validation',
      title: 'Demand Validation',
      description: 'Min/max demand and increment rules',
      rules: {
        min_demand: 'Minimum Demand',
        max_demand: 'Maximum Demand',
        demand_increment: 'Demand Increment',
      },
    },
    {
      key: 'conversion_validation',
      title: 'Conversion Validation',
      description: 'Conversion rates and quantity validation',
      rules: {
        conversion_rate_range: 'Conversion Rate Range',
        material_quantity_positive: 'Material Quantity Positive',
        scrap_rate_range: 'Scrap Rate Range',
      },
    },
    {
      key: 'timing_validation',
      title: 'Timing Validation',
      description: 'Duration and time relationship validation',
      rules: {
        duration_positive: 'Duration Positive',
        lead_time_minimum: 'Lead Time Minimum',
        cycle_time_vs_duration: 'Cycle Time vs Duration',
      },
    },
    {
      key: 'capacity_validation',
      title: 'Capacity Validation',
      description: 'Capacity limits and utilization warnings',
      rules: {
        max_capacity: 'Max Capacity',
        utilization_warning: 'Utilization Warning',
      },
    },
    {
      key: 'quality_validation',
      title: 'Quality Validation',
      description: 'Defect and loss rate thresholds',
      rules: {
        defect_rate_threshold: 'Defect Rate Threshold',
        combined_loss_rate: 'Combined Loss Rate',
      },
    },
    {
      key: 'inventory_validation',
      title: 'Inventory Validation',
      description: 'Buffer and reorder point validation',
      rules: {
        buffer_relationship: 'Buffer Relationship',
        reorder_point_range: 'Reorder Point Range',
      },
    },
    {
      key: 'state_transition_validation',
      title: 'State Transition Validation',
      description: 'State transition requirements',
      rules: {
        from_state_required: 'From State Required',
        to_state_required: 'To State Required',
        same_state_allowed: 'Same State Allowed',
      },
    },
    {
      key: 'business_rules',
      title: 'Business Rules',
      description: 'Custom business validation rules',
      rules: {
        minimum_order_value: 'Minimum Order Value',
        working_hours: 'Working Hours',
        available_days: 'Available Days',
      },
    },
    {
      key: 'custom_validation',
      title: 'Custom Validation',
      description: 'Custom validation rules including improvement artifact checks and other company-specific validations',
      rules: {} as Record<string, string>, // All rules are custom
    },
  ];

  const handleRuleChange = (categoryKey: keyof ValidationRules, ruleKey: string, value: ValidationRule) => {
    onChange({
      ...validationRules,
      [categoryKey]: {
        ...(validationRules[categoryKey] || {}),
        [ruleKey]: value,
      },
    });
  };

  const handleDeleteRule = (categoryKey: keyof ValidationRules, ruleKey: string) => {
    const categoryRules = { ...(validationRules[categoryKey] || {}) };
    delete (categoryRules as any)[ruleKey];
    
    onChange({
      ...validationRules,
      [categoryKey]: categoryRules,
    });
  };

  const handleAddCustomRule = (categoryKey: keyof ValidationRules) => {
    const customKey = `custom_rule_${Date.now()}`;
    // For property_validation, default to property-based rule; otherwise use number
    const defaultType = categoryKey === 'property_validation' && artifactProperties.length > 0
      ? 'property'
      : 'number';
    
    const newRule: ValidationRule = defaultType === 'property' ? {
      type: 'property',
      property_key: '',
      operator: '==',
      value: null,
      severity: 'error',
    } : {
      type: 'number',
      operator: '>=',
      value: 0,
      severity: 'error',
    };

    onChange({
      ...validationRules,
      [categoryKey]: {
        ...(validationRules[categoryKey] || {}),
        [customKey]: newRule,
      },
    });
  };

  const renderCategoryPanel = (category: typeof categories[0]) => {
    const categoryKey = category.key as keyof ValidationRules;
    const categoryRules = (validationRules[categoryKey] as any) || {};

    return (
      <Panel
        key={category.key}
        header={
          <div className="panel-header">
            <span className="panel-title">{category.title}</span>
            <span className="panel-description">{category.description}</span>
          </div>
        }
      >
        <div className="category-rules">
          {/* Predefined rules */}
          {Object.entries(category.rules).map(([ruleKey, ruleLabel]) => {
            const ruleValue = categoryRules[ruleKey];
            
            if (!ruleValue) return null;

            return (
              <div key={ruleKey} className="rule-item">
                <ValidationRuleEditor
                  label={ruleLabel as string}
                  rule={ruleValue}
                  onChange={(value) => handleRuleChange(categoryKey, ruleKey, value)}
                  disabled={disabled}
                  artifactProperties={artifactProperties}
                  transitions={transitions}
                />
                {!disabled && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteRule(categoryKey, ruleKey)}
                    className="delete-rule-btn"
                  />
                )}
              </div>
            );
          })}

          {/* Custom rules */}
          {Object.entries(categoryRules)
            .filter(([key]) => !(category.rules as any)[key])
            .map(([ruleKey, ruleValue]) => (
              <div key={ruleKey} className="rule-item">
                <ValidationRuleEditor
                  label={ruleKey}
                  rule={ruleValue as ValidationRule}
                  onChange={(value) => handleRuleChange(categoryKey, ruleKey, value)}
                  disabled={disabled}
                  artifactProperties={artifactProperties}
                  transitions={transitions}
                />
                {!disabled && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteRule(categoryKey, ruleKey)}
                    className="delete-rule-btn"
                  />
                )}
              </div>
            ))}

          {/* Add Rule Button */}
          {!disabled && (
            <div className="add-rule-section">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => handleAddCustomRule(categoryKey)}
                block
              >
                {categoryKey === 'property_validation' 
                  ? 'Add Transition Condition' 
                  : 'Add Custom Rule'}
              </Button>
            </div>
          )}
        </div>
      </Panel>
    );
  };

  return (
    <div className="validation-rules-editor">
      <div className="editor-header">
        <h3>
          Validation Rules
          <Tooltip title="Define validation rules to ensure data quality and business logic compliance">
            <InfoCircleOutlined style={{ marginLeft: 8, fontSize: 14, color: '#faad14' }} />
          </Tooltip>
        </h3>
      </div>

      <Collapse
        activeKey={activeKey}
        onChange={(keys) => setActiveKey(keys as string[])}
        className="validation-collapse"
        defaultActiveKey={artifactProperties.length > 0 ? ['property_validation'] : ['conversion']}
      >
        {categories.map((category) => renderCategoryPanel(category))}
      </Collapse>
    </div>
  );
};

